import { useState, useEffect, useRef } from "react";
import { Header, BottomNav } from "../components/Header";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Edit2, Trash2, Image, ArrowUp, ArrowDown, ExternalLink, Upload, Loader2, Images, MousePointer, FileText, Navigation, Package, Link2 } from "lucide-react";

// Predefined action options for banner buttons
const ACTION_OPTIONS = {
  none: { label: "Sin acción", icon: MousePointer, description: "El botón no hace nada" },
  popup: { label: "Abrir Popup", icon: FileText, description: "Abre una ventana modal" },
  navigate: { label: "Navegar a sección", icon: Navigation, description: "Ir a una sección de la app" },
  product: { label: "Ver producto", icon: Package, description: "Ir a un producto específico" },
  external_url: { label: "URL externa", icon: Link2, description: "Abrir enlace externo" }
};

// Predefined popup options
const POPUP_OPTIONS = [
  { value: "terms", label: "Términos y Condiciones" },
  { value: "privacy", label: "Política de Privacidad" },
  { value: "faq", label: "Preguntas Frecuentes (FAQ)" },
  { value: "contact", label: "Contacto" },
  { value: "how_to_buy", label: "¿Cómo Comprar?" }
];

// Predefined navigation options
const NAVIGATE_OPTIONS = [
  { value: "/section/auctions", label: "Ver todas las Subastas" },
  { value: "/section/sales", label: "Ver todas las Ventas Directas" },
  { value: "/", label: "Ir al Inicio" }
];

// Predefined banner images
const PRESET_BANNER_IMAGES = [
  {
    name: "Subastas (Amarillo)",
    url: "https://static.prod-images.emergentagent.com/jobs/ccad84ef-5660-4241-8d7f-42105fe20933/images/c146a91efe7f75813ed106e3b4521e8c12f8d945af37130fc4f2aa5a23a92d3c.png",
    suggestedTitle: "¡Oferta y Gana!",
    suggestedSubtitle: "Los mejores smartphones al mejor precio"
  },
  {
    name: "Pago Contraentrega (Verde)",
    url: "https://static.prod-images.emergentagent.com/jobs/ccad84ef-5660-4241-8d7f-42105fe20933/images/82eb6080eeccfc67dab391eec47a1e33f0ee8371788580bb870ad91a4bdbdf72.png",
    suggestedTitle: "Pago Seguro",
    suggestedSubtitle: "Paga solo cuando recibas tu producto"
  },
  {
    name: "Bienvenida Clueer (Azul)",
    url: "https://static.prod-images.emergentagent.com/jobs/ccad84ef-5660-4241-8d7f-42105fe20933/images/d5a3d10ae8787c4ab9e2660d33133a5a6cc31bbef9439abd718ba371119ccdb5.png",
    suggestedTitle: "¡Bienvenido a Clueer!",
    suggestedSubtitle: "Tu marketplace de subastas en Panamá"
  }
];

const AdminBannersPage = () => {
  const { user, token } = useAuth();
  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    image_url: "",
    action_type: "none",
    action_value: "",
    link_url: "",
    button_text: "",
    is_active: true,
    order: 0
  });

  useEffect(() => {
    fetchBanners();
    fetchProducts();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/banners?active_only=false`);
      setBanners(response.data);
    } catch (error) {
      toast.error("Error al cargar banners");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      // Filter only active products (not ended)
      const activeProducts = response.data.filter(p => 
        p.status !== "ended" && p.status !== "sold"
      );
      setProducts(activeProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Por favor selecciona una imagen");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen debe ser menor a 5MB");
      return;
    }

    setUploading(true);
    try {
      const formDataImg = new FormData();
      formDataImg.append('file', file);
      
      const response = await axios.post(`${API}/upload/image`, formDataImg, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        },
        withCredentials: true
      });
      
      setFormData({ ...formData, image_url: response.data.url });
      toast.success("Imagen subida exitosamente");
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error(error.response?.data?.detail || "Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingBanner(null);
    setFormData({
      title: "",
      subtitle: "",
      image_url: "",
      action_type: "none",
      action_value: "",
      link_url: "",
      button_text: "",
      is_active: true,
      order: banners.length
    });
    setDialogOpen(true);
  };

  const openEditDialog = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      subtitle: banner.subtitle || "",
      image_url: banner.image_url,
      action_type: banner.action_type || "none",
      action_value: banner.action_value || "",
      link_url: banner.link_url || "",
      button_text: banner.button_text || "",
      is_active: banner.is_active,
      order: banner.order
    });
    setDialogOpen(true);
  };

  // Helper to get action description for display
  const getActionDescription = (banner) => {
    const actionType = banner.action_type || "none";
    if (actionType === "none") return null;
    
    if (actionType === "popup") {
      const popup = POPUP_OPTIONS.find(p => p.value === banner.action_value);
      return popup ? `Popup: ${popup.label}` : "Popup";
    }
    if (actionType === "navigate") {
      const nav = NAVIGATE_OPTIONS.find(n => n.value === banner.action_value);
      return nav ? nav.label : banner.action_value;
    }
    if (actionType === "product") {
      const product = products.find(p => p.product_id === banner.action_value);
      return product ? `Producto: ${product.title}` : "Producto";
    }
    if (actionType === "external_url") {
      return "URL externa";
    }
    return null;
  };

  const handleSave = async () => {
    if (!formData.image_url) {
      toast.error("La imagen es requerida");
      return;
    }

    setSaving(true);
    try {
      if (editingBanner) {
        await axios.put(
          `${API}/admin/banners/${editingBanner.banner_id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );
        toast.success("Banner actualizado");
      } else {
        await axios.post(
          `${API}/admin/banners`,
          formData,
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );
        toast.success("Banner creado");
      }
      setDialogOpen(false);
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm("¿Eliminar este banner?")) return;
    
    try {
      await axios.delete(
        `${API}/admin/banners/${bannerId}`,
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success("Banner eliminado");
      fetchBanners();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      await axios.put(
        `${API}/admin/banners/${banner.banner_id}`,
        { ...banner, is_active: !banner.is_active },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      fetchBanners();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <p className="text-[#64748B]">Acceso denegado</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Header />
      <main className="max-w-5xl mx-auto pt-32 pb-24 px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A]">Banners</h1>
            <p className="text-sm text-[#64748B]">Gestiona los banners del carrusel</p>
          </div>
          <Button onClick={openCreateDialog} className="rounded-full bg-[#F97316] hover:bg-[#EA580C]">
            <Plus size={18} className="mr-2" />
            Nuevo Banner
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : banners.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
            <Image size={48} className="mx-auto text-[#CBD5E1] mb-4" />
            <h3 className="font-semibold text-[#0F172A] mb-2">No hay banners</h3>
            <p className="text-[#64748B] text-sm mb-4">Crea tu primer banner para mostrarlo en el carrusel</p>
            <Button onClick={openCreateDialog} className="rounded-full bg-[#F97316] hover:bg-[#EA580C]">
              <Plus size={18} className="mr-2" />
              Crear Banner
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {banners.map((banner, index) => (
              <div
                key={banner.banner_id}
                className={`bg-white rounded-xl shadow-sm overflow-hidden ${!banner.is_active ? 'opacity-60' : ''}`}
              >
                <div className="flex gap-4 p-4">
                  {/* Preview image */}
                  <div
                    className="w-32 h-20 rounded-lg bg-cover bg-center flex-shrink-0"
                    style={{ backgroundImage: `url(${banner.image_url})` }}
                  />
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-[#0F172A] truncate">{banner.title || <span className="text-[#94A3B8] italic">Sin título (banner limpio)</span>}</h3>
                        {banner.subtitle && (
                          <p className="text-sm text-[#64748B] truncate">{banner.subtitle}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${banner.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {banner.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                        <span className="text-xs text-[#94A3B8]">#{index + 1}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-3">
                      {getActionDescription(banner) && (
                        <span className="text-xs text-blue-600 flex items-center gap-1">
                          {ACTION_OPTIONS[banner.action_type || "none"]?.icon && (
                            <span className="w-3 h-3">
                              {(() => {
                                const Icon = ACTION_OPTIONS[banner.action_type || "none"]?.icon;
                                return Icon ? <Icon size={12} /> : null;
                              })()}
                            </span>
                          )}
                          {getActionDescription(banner)}
                        </span>
                      )}
                      {banner.link_url && !banner.action_type && (
                        <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 flex items-center gap-1">
                          <ExternalLink size={12} />
                          Ver enlace
                        </a>
                      )}
                      {banner.button_text && (
                        <span className="text-xs text-[#64748B]">
                          Botón: "{banner.button_text}"
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Switch
                      checked={banner.is_active}
                      onCheckedChange={() => handleToggleActive(banner)}
                    />
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(banner)}>
                      <Edit2 size={16} className="text-[#64748B]" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(banner.banner_id)}>
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingBanner ? "Editar Banner" : "Nuevo Banner"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Preview */}
              {formData.image_url && (
                <div
                  className="w-full h-32 rounded-xl bg-cover bg-center relative"
                  style={{ backgroundImage: `url(${formData.image_url})` }}
                >
                  {(formData.title || formData.subtitle) && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent rounded-xl" />
                      <div className="absolute inset-0 flex flex-col justify-end p-4">
                        {formData.title && <h4 className="text-white font-bold">{formData.title}</h4>}
                        {formData.subtitle && <p className="text-white/90 text-sm">{formData.subtitle}</p>}
                      </div>
                    </>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Título <span className="text-[#94A3B8] text-xs">(opcional)</span></Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Déjalo vacío para un banner limpio, sin texto superpuesto"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label>Subtítulo</Label>
                  <Input
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    placeholder="Hasta 50% de descuento"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label>Imagen del Banner *</Label>
                  <div className="space-y-3">
                    {/* Preset images */}
                    <div>
                      <p className="text-xs text-[#64748B] mb-2 flex items-center gap-1">
                        <Images size={12} />
                        Imágenes predefinidas:
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {PRESET_BANNER_IMAGES.map((preset, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setFormData({
                              ...formData,
                              image_url: preset.url,
                              title: formData.title || preset.suggestedTitle,
                              subtitle: formData.subtitle || preset.suggestedSubtitle
                            })}
                            className={`relative aspect-[3/1] rounded-lg overflow-hidden border-2 transition-all ${formData.image_url === preset.url ? 'border-[#F97316] ring-2 ring-[#F97316]/20' : 'border-[#E2E8F0] hover:border-[#94A3B8]'}`}
                          >
                            <img src={preset.url} alt={preset.name} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 flex items-end p-1">
                              <span className="text-[8px] text-white truncate">{preset.name}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Image upload button */}
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/*"
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex-1"
                      >
                        {uploading ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            Subiendo...
                          </>
                        ) : (
                          <>
                            <Upload size={16} className="mr-2" />
                            Subir imagen propia
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-[11px] text-[#94A3B8]" data-testid="banner-size-hint">
                      📐 Tamaño recomendado: <strong>1200 x 400 px</strong> (proporción 3:1). Así tu banner se verá completo sin recortes en móvil y escritorio.
                    </p>
                    
                    {/* Or paste URL */}
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8]">URL:</span>
                      <Input
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        placeholder="https://..."
                        className="pl-12"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="col-span-2">
                  <Label>Acción del Botón</Label>
                  <Select
                    value={formData.action_type}
                    onValueChange={(value) => setFormData({ 
                      ...formData, 
                      action_type: value,
                      action_value: "",
                      link_url: ""
                    })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Selecciona una acción" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ACTION_OPTIONS).map(([key, opt]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <opt.icon size={14} />
                            <span>{opt.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#94A3B8] mt-1">
                    {ACTION_OPTIONS[formData.action_type]?.description}
                  </p>
                </div>

                {/* Popup selector */}
                {formData.action_type === "popup" && (
                  <div className="col-span-2">
                    <Label>Selecciona el Popup</Label>
                    <Select
                      value={formData.action_value}
                      onValueChange={(value) => setFormData({ ...formData, action_value: value })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Elige un popup..." />
                      </SelectTrigger>
                      <SelectContent>
                        {POPUP_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Navigation selector */}
                {formData.action_type === "navigate" && (
                  <div className="col-span-2">
                    <Label>Selecciona la Sección</Label>
                    <Select
                      value={formData.action_value}
                      onValueChange={(value) => setFormData({ ...formData, action_value: value })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Elige una sección..." />
                      </SelectTrigger>
                      <SelectContent>
                        {NAVIGATE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Product selector */}
                {formData.action_type === "product" && (
                  <div className="col-span-2">
                    <Label>Selecciona el Producto</Label>
                    <Select
                      value={formData.action_value}
                      onValueChange={(value) => setFormData({ ...formData, action_value: value })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Elige un producto..." />
                      </SelectTrigger>
                      <SelectContent>
                        {products.length === 0 ? (
                          <SelectItem value="" disabled>No hay productos disponibles</SelectItem>
                        ) : (
                          products.map((product) => (
                            <SelectItem key={product.product_id} value={product.product_id}>
                              <div className="flex items-center gap-2">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                  product.sale_type === 'auction' 
                                    ? 'bg-amber-100 text-amber-700' 
                                    : 'bg-emerald-100 text-emerald-700'
                                }`}>
                                  {product.sale_type === 'auction' ? 'Subasta' : 'Venta'}
                                </span>
                                <span className="truncate max-w-[200px]">{product.title}</span>
                                <span className="text-[#94A3B8] text-xs">${product.current_price || product.buy_now_price}</span>
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* External URL input */}
                {formData.action_type === "external_url" && (
                  <div className="col-span-2">
                    <Label>URL Externa</Label>
                    <Input
                      value={formData.action_value}
                      onChange={(e) => setFormData({ ...formData, action_value: e.target.value })}
                      placeholder="https://example.com"
                    />
                  </div>
                )}
                
                <div>
                  <Label>Texto del botón</Label>
                  <Input
                    value={formData.button_text}
                    onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                    placeholder="Ver ofertas"
                  />
                </div>
                
                <div>
                  <Label>Orden</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    min={0}
                  />
                </div>
                
                <div className="col-span-2 flex items-center justify-between">
                  <Label>Activo</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="rounded-full bg-[#F97316] hover:bg-[#EA580C]">
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
      <BottomNav />
    </div>
  );
};

export default AdminBannersPage;
